/**
 * Product rating CTA: write-review for signed-in customers.
 *
 * Liquid `customer` is authoritative on the shop domain.
 * CLI theme.preview (127.0.0.1) cannot receive New Customer Accounts sessions
 * (header stays on "Log in"), so local preview shows Rate unless
 * ?gma_force_guest_rating=1 is present.
 *
 * Rate this product opens Judge.me's in-store write-review popup
 * (review_form_location: in_store_popup / modal_write_review_flow).
 *
 * Organised PDPs: Judge.me storefront widgets (floating tab, all-reviews,
 * carousels) are removed from the DOM — not CSS-hidden. A clipped Rate host
 * remains only to boot the write-modal until the merchant disables Judge.me
 * storefront widgets at go-live.
 */
(function () {
  const JUDGEME_HOST_ID = 'judgeme_product_reviews';

  /** App-embed / theme-block storefront chrome — remove on organised (not the Rate host). */
  const JUDGEME_WIDGET_REMOVE_SELECTOR = [
    '.jdgm-revs-tab',
    '.jdgm-all-reviews-page',
    '.jdgm-all-reviews-widget',
    '.jdgm-all-reviews-widget-v2025',
    '.jdgm-carousel-wrapper',
  ].join(', ');

  /**
   * @returns {boolean}
   */
  function isLocalThemePreview() {
    const host = window.location.hostname;
    return host === '127.0.0.1' || host === 'localhost';
  }

  /**
   * @returns {boolean}
   */
  function forceGuestFromQuery() {
    try {
      return new URLSearchParams(window.location.search).get('gma_force_guest_rating') === '1';
    } catch (_err) {
      return false;
    }
  }

  /**
   * @returns {boolean}
   */
  function isOrganisedNoWidgetsLayout() {
    return Boolean(
      document.querySelector('.product--organised') ||
        document.querySelector('[data-gma-no-judgeme-widgets="true"]')
    );
  }

  /**
   * @returns {boolean}
   */
  function customerLoggedInSync() {
    const page = window.ShopifyAnalytics?.meta?.page || window.meta?.page;
    if (page?.customerId) return true;

    if (typeof window.__st !== 'undefined' && window.__st.cid) return true;

    const accountLink = document.querySelector('a.header__icon--account, a.menu-drawer__account');
    if (accountLink instanceof HTMLAnchorElement) {
      const href = accountLink.getAttribute('href') || '';
      const label = (accountLink.getAttribute('aria-label') || accountLink.textContent || '').toLowerCase();
      const looksLikeLogin =
        href.includes('customer_authentication') ||
        href.includes('/login') ||
        href.includes('account/login') ||
        label.includes('log in') ||
        label.includes('sign in');
      if (href && !looksLikeLogin) return true;
    }

    return false;
  }

  /**
   * @param {HTMLElement} root
   */
  function applyCustomerState(root) {
    let signedIn = root.dataset.liquidCustomer === 'true' || customerLoggedInSync();

    if (forceGuestFromQuery()) {
      signedIn = false;
    } else if (!signedIn && isLocalThemePreview()) {
      // NCA cookies live on the shop domain; CLI preview never gets Liquid customer.
      signedIn = true;
    }

    root.dataset.sessionCustomer = signedIn ? 'true' : 'false';

    const guest = root.querySelector('[data-gma-rating-guest]');
    const member = root.querySelector('[data-gma-rating-customer]');
    if (!(guest instanceof HTMLElement) || !(member instanceof HTMLElement)) return;

    guest.hidden = signedIn;
    member.hidden = !signedIn;
  }

  /**
   * @param {number} ms
   * @returns {Promise<void>}
   */
  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  /**
   * Remove Judge.me storefront widgets from organised PDPs.
   * Preserves #judgeme_product_reviews (Rate host) and write-review modal nodes.
   */
  function removeJudgemeStorefrontWidgets() {
    if (!isOrganisedNoWidgetsLayout()) return;

    document.querySelectorAll(JUDGEME_WIDGET_REMOVE_SELECTOR).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.closest('.jdgm-write-review-modal')) return;
      if (node.closest(`#${JUDGEME_HOST_ID}`)) return;
      node.remove();
    });
  }

  function watchJudgemeWidgetInjection() {
    if (!isOrganisedNoWidgetsLayout()) return;

    removeJudgemeStorefrontWidgets();

    const observer = new MutationObserver(() => {
      removeJudgemeStorefrontWidgets();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  /**
   * @returns {HTMLElement|null}
   */
  function ensureJudgemeHost(productId) {
    let host = document.getElementById(JUDGEME_HOST_ID);
    if (!(host instanceof HTMLElement)) {
      host = document.createElement('div');
      host.id = JUDGEME_HOST_ID;
      host.className = 'jdgm-widget jdgm-review-widget gma-product-rating__judgeme-host';
      host.setAttribute('data-auto-install', 'false');
      host.hidden = true;
      document.body.appendChild(host);
    }

    host.classList.add('jdgm-widget', 'jdgm-review-widget');
    if (productId) host.setAttribute('data-id', String(productId));
    return host;
  }

  /**
   * @param {HTMLElement} host
   * @returns {Promise<boolean>}
   */
  async function ensureJudgemeWidgetReady(host) {
    if (typeof window.jdgm?.initializeWidgets === 'function') {
      try {
        window.jdgm.initializeWidgets();
      } catch (_err) {
        /* Judge.me optional */
      }
    }

    for (let attempt = 0; attempt < 20; attempt += 1) {
      if (host.querySelector('.jdgm-rev-widg, .jdgm-write-rev-link')) return true;
      if (typeof window.jdgm?._WriteReviewModal === 'function' && window.jdgm?.$) return true;
      await wait(150);
    }

    return typeof window.jdgm?._WriteReviewModal === 'function' && !!window.jdgm?.$;
  }

  /**
   * @param {HTMLElement} host
   * @returns {Promise<boolean>}
   */
  async function setupJudgemeWriteModal(host) {
    const jdgm = window.jdgm;
    if (!jdgm?.$ || typeof jdgm._renderAndSetupReviewForm !== 'function') return false;

    const $host = jdgm.$(host);
    try {
      await Promise.resolve(jdgm._renderAndSetupReviewForm($host));
      return true;
    } catch (_err) {
      return false;
    }
  }

  /**
   * @returns {boolean}
   */
  function clickJudgemeWriteLink(host) {
    const link =
      host.querySelector('.jdgm-write-rev-link') ||
      document.querySelector(`#${JUDGEME_HOST_ID} .jdgm-write-rev-link`) ||
      document.querySelector('.jdgm-rev-widg .jdgm-write-rev-link');

    if (!(link instanceof HTMLElement)) return false;
    link.click();
    return true;
  }

  /**
   * @param {string|number} productId
   * @returns {Promise<boolean>}
   */
  async function openJudgemeModalDirect(productId) {
    const jdgm = window.jdgm;
    if (!jdgm?.$ || typeof jdgm._WriteReviewModal !== 'function') return false;

    try {
      const modal = new jdgm._WriteReviewModal(jdgm.$);
      const ok = await modal.setup('jdgm-rev-widg', productId);
      if (!ok) return false;
      modal.showModalPage();
      return true;
    } catch (_err) {
      return false;
    }
  }

  /**
   * @returns {boolean}
   */
  function judgemeModalIsOpen() {
    const modal = document.querySelector('.jdgm-write-review-modal');
    if (!(modal instanceof HTMLElement)) return false;
    return window.getComputedStyle(modal).display !== 'none';
  }

  /**
   * @param {HTMLElement} trigger
   * @param {string|number|undefined} productId
   */
  async function openWriteReviewWithHost(trigger, productId) {
    const host = ensureJudgemeHost(productId);
    if (!(host instanceof HTMLElement)) return;

    await ensureJudgemeWidgetReady(host);
    await setupJudgemeWriteModal(host);
    await wait(250);

    clickJudgemeWriteLink(host);
    await wait(500);

    if (!judgemeModalIsOpen()) {
      clickJudgemeWriteLink(host);
      await wait(500);
    }

    if (!judgemeModalIsOpen() && productId) {
      const opened = await openJudgemeModalDirect(productId);
      if (opened) await wait(400);
    }
  }

  /**
   * @param {HTMLElement} trigger
   */
  async function openWriteReview(trigger) {
    const root = trigger.closest('[data-gma-product-rating]');
    const productId =
      root instanceof HTMLElement && root.dataset.productId
        ? root.dataset.productId
        : window.meta?.product?.id || window.ShopifyAnalytics?.meta?.product?.id;

    await openWriteReviewWithHost(trigger, productId);
  }

  function onClick(event) {
    const button = event.target.closest('[data-gma-write-review]');
    if (!(button instanceof HTMLElement)) return;
    event.preventDefault();
    openWriteReview(button);
  }

  function refreshAll() {
    document.querySelectorAll('[data-gma-product-rating]').forEach((root) => {
      if (root instanceof HTMLElement) applyCustomerState(root);
    });
  }

  document.addEventListener('click', onClick);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshAll();
  });
  window.addEventListener('focus', refreshAll);

  function onReady() {
    watchJudgemeWidgetInjection();
    refreshAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  window.setTimeout(refreshAll, 800);
})();
